import type { IGameRecord } from '../interfaces/IGameRecord';

export interface GameRecordOptions {
  /** Include ties in the denominator when calculating Pct (true for NFL, false for NBA/NHL/MLB) */
  includeTiesInDenominator: boolean;
  /** Value to return from Pct when there are 0 games (typically 0) */
  zeroGamesValue: number;
  /** Value to return from PctNaN when there are 0 games (typically -1) */
  zeroPctValue: number;
}

const defaultOptions: GameRecordOptions = {
  includeTiesInDenominator: true,
  zeroGamesValue: 0,
  zeroPctValue: -1,
};

export class GameRecordBase implements IGameRecord {
  Wins: KnockoutObservable<number>;
  Losses: KnockoutObservable<number>;
  Ties: KnockoutObservable<number>;
  RecordString: KnockoutComputed<string>;
  Pct: KnockoutComputed<number>;
  PctNaN: KnockoutComputed<number>;
  Differential: KnockoutComputed<number>;

  private readonly opts: GameRecordOptions;

  constructor(fakeObs: KnockoutObservable<number | undefined>, opts: Partial<GameRecordOptions> = {}) {
    this.opts = { ...defaultOptions, ...opts };

    this.Wins = ko.observable(0);
    this.Losses = ko.observable(0);
    this.Ties = ko.observable(0);

    this.RecordString = ko.computed(() => this.createToString());

    // toString is also a KO computed to enable reactive binding
    (this as unknown as { toString: KnockoutComputed<string> }).toString = ko.pureComputed(() =>
      this.createToString()
    );

    this.Pct = ko.pureComputed(() => {
      fakeObs();
      return this.calcWLT();
    });

    this.PctNaN = ko.pureComputed(() => {
      fakeObs();
      return this.calcPct();
    });

    this.Differential = ko.pureComputed(() => {
      return this.Wins() - this.Losses();
    });
  }

  protected createToString(): string {
    let retStr = `${this.Wins()}-${this.Losses()}`;
    if (this.Ties() > 0) {
      retStr += `-${this.Ties()}`;
    }
    return retStr;
  }

  private calcWLT(): number {
    const denom = this.opts.includeTiesInDenominator
      ? this.Wins() + this.Losses() + this.Ties()
      : this.Wins() + this.Losses();

    if (denom === 0) {
      return this.opts.zeroGamesValue;
    }
    return (this.Wins() + 0.5 * this.Ties()) / denom;
  }

  private calcPct(): number {
    const denom = this.opts.includeTiesInDenominator
      ? this.Wins() + this.Losses() + this.Ties()
      : this.Wins() + this.Losses();

    if (denom === 0) {
      return this.opts.zeroPctValue;
    }
    return (this.Wins() + 0.5 * this.Ties()) / denom;
  }

  toString(): string {
    return this.createToString();
  }

  resetWins(): void {
    this.Wins(0);
  }

  resetLosses(): void {
    this.Losses(0);
  }

  resetTies(): void {
    this.Ties(0);
  }
}
